import {
  Box,
  Disclaimer,
  DismissedForGoodIcon,
  Div,
  H5,
  Pill,
  TooltipWrapper,
  TruncatedText,
  lightTheme,
  renderRole,
} from '@vgrubs/components';
import type {
  AnnouncementViewer,
  AnnouncementViewerSeenOn,
} from '../../../types/api/announcements.types';
import { ConversationAvatar } from '../../Chat/components/ConversationAvatar';
import {
  formatDay,
  formatSeenAt,
  formatSeenAtFull,
  pairLabel,
  viewerName,
} from '../helpers/announcementViewers';

/** Widths shared with the table's header, so the columns line up. */
export const VIEWER_COLUMNS = [
  { label: 'User', width: '32%' },
  { label: 'Seen on', width: '32%' },
  { label: 'First seen', width: '13%' },
  { label: 'Last seen', width: '13%' },
  { label: 'Views', width: '10%' },
];

type Props = {
  viewer: AnnouncementViewer;
};

/**
 * One person: who they are, where they saw it and how often. Columns on
 * desktop; on a phone the same facts stacked, the way the referrals list does.
 */
export const AnnouncementViewerRow = ({ viewer }: Props) => (
  <Box
    $mb="8"
    $flex
    $alignCenter
    $gap="32px"
    $gapSM="12px"
    $columnSM
    $alignStartSM
  >
    <Div $width={VIEWER_COLUMNS[0].width} $widthSM="100%" $minWidth="0">
      <ViewerCell viewer={viewer} />
    </Div>
    <Div $width={VIEWER_COLUMNS[1].width} $widthSM="100%">
      <SeenOnChips seenOn={viewer.seenOn} />
    </Div>
    <Div $desktop $width={VIEWER_COLUMNS[2].width}>
      <TooltipWrapper tooltip={formatSeenAtFull(viewer.firstSeenAt)}>
        <Disclaimer $m="0">{formatDay(viewer.firstSeenAt)}</Disclaimer>
      </TooltipWrapper>
    </Div>
    <Div $desktop $width={VIEWER_COLUMNS[3].width}>
      <TooltipWrapper tooltip={formatSeenAtFull(viewer.lastSeenAt)}>
        <Disclaimer $m="0">{formatSeenAt(viewer.lastSeenAt)}</Disclaimer>
      </TooltipWrapper>
    </Div>
    <Div $desktop $width={VIEWER_COLUMNS[4].width}>
      <H5 $m="0">{viewer.viewCount}</H5>
    </Div>

    <Div $mobile>
      <Disclaimer $m="0" $color={lightTheme.lightGray}>
        First {formatDay(viewer.firstSeenAt)} · Last{' '}
        {formatSeenAt(viewer.lastSeenAt)} ·{' '}
        {viewer.viewCount === 1 ? '1 view' : `${viewer.viewCount} views`}
      </Disclaimer>
    </Div>
  </Box>
);

/**
 * Initials, name, role - and the crossed eye when they told it to stop
 * showing on at least one app. The tooltip names which, since the dismissal
 * is per app and platform and the rest may still show it.
 */
const ViewerCell = ({ viewer }: Props) => {
  const name = viewerName(viewer);
  const dismissedOn = viewer.seenOn
    .filter((pair) => pair.dontShowAgain)
    .map((pair) => pairLabel(pair.app, pair.platform))
    .join(', ');

  return (
    <Div $flex $alignCenter $gap="12px" $minWidth="0">
      <ConversationAvatar name={name} size={32} />
      <Div $flex $column $gap="2px" $minWidth="0">
        <Div $flex $alignCenter $gap="6px" $minWidth="0">
          <H5 $m="0" style={{ minWidth: 0 }}>
            <TruncatedText>{name}</TruncatedText>
          </H5>
          {viewer.dontShowAgain && (
            <TooltipWrapper
              tooltip={`Dismissed for good on ${dismissedOn || 'one app'}`}
              style={{ display: 'flex' }}
            >
              <DismissedForGoodIcon />
            </TooltipWrapper>
          )}
        </Div>
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          <TruncatedText>
            {[renderRole(viewer.role), viewer.email]
              .filter(Boolean)
              .join(' · ')}
          </TruncatedText>
        </Disclaimer>
      </Div>
    </Div>
  );
};

/**
 * What a crossed-out pill means, on hover. The API keeps no time for the
 * dismissal itself, only for the last view on that pair, so that is the date
 * given; a pair with no views was dismissed without being opened.
 */
const dismissedTooltip = (pair: AnnouncementViewerSeenOn) => {
  const where = pairLabel(pair.app, pair.platform);
  return pair.viewCount
    ? `Dismissed for good on ${where}. Last seen there ${formatSeenAtFull(
        pair.seenAt
      )}`
    : `Dismissed for good on ${where} without viewing it`;
};

/** "merchant iOS ×4" per app and platform; a bare dismissal says so instead. */
const SeenOnChips = ({ seenOn }: { seenOn: AnnouncementViewerSeenOn[] }) => (
  <Div $flex $wrap $gap="6px">
    {seenOn.map((pair) => (
      <TooltipWrapper
        key={`${pair.app}_${pair.platform}`}
        tooltip={pair.dontShowAgain && dismissedTooltip(pair)}
      >
        <Pill
          $background={lightTheme.gray}
          $minWidth="0"
          $minWidthSM="0"
          $gap="4px"
        >
          <Disclaimer $m="0" $color={lightTheme.darkGray}>
            {pairLabel(pair.app, pair.platform)}{' '}
            {pair.viewCount ? `×${pair.viewCount}` : '· dismissed'}
          </Disclaimer>
          {pair.dontShowAgain && pair.viewCount > 0 && (
            <DismissedForGoodIcon size={12} />
          )}
        </Pill>
      </TooltipWrapper>
    ))}
  </Div>
);
